import { useEffect, useMemo, useState } from "react";
import "./Admin.css";
import TipTapEditor from "../TipTap/TipTapEditor";

/* -------------------------------------------------------
   TEMPLATE
------------------------------------------------------- */
const emptyPost = {
  id: null,
  slug: "",
  title: "",
  status: "published",
  excerpt: "",
  body: "",
  category: "",
  tags: [],
  coverImageUrl: "",
  author: "",
  publishedDate: "",
};

function slugify(str) {
  return String(str || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

/* -------------------------------------------------------
   COMPONENT
------------------------------------------------------- */
export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [mode, setMode] = useState("list"); // "list" | "add" | "edit"
  const [search, setSearch] = useState("");

  const [form, setForm] = useState(emptyPost);
  const [, setEditingId] = useState(null);
  const [slugTouched, setSlugTouched] = useState(false);

  const [tagInput, setTagInput] = useState("");

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Comment moderation (edit mode)
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState(null);

  /* ------------------------------ LOAD POSTS ------------------------------ */
  useEffect(() => {
    async function load() {
      try {
        const res = await fetch("/.netlify/functions/get-posts", {
          credentials: "include",
        });
        const json = await res.json();
        if (!res.ok || !json.ok) {
          setLoadError(json.error || "Failed to load posts.");
        } else {
          setPosts(json.posts || []);
        }
      } catch (err) {
        setLoadError("Network error while loading posts.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  /* ------------------------------ FILTER ------------------------------ */
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return posts;
    return posts.filter((p) =>
      [p.title, p.category, p.tags?.join(" ")]
        .filter(Boolean)
        .some((s) => s.toLowerCase().includes(q))
    );
  }, [posts, search]);

  /* ------------------------------ MODE HELPERS ------------------------------ */
  function startAdd() {
    setForm(emptyPost);
    setEditingId(null);
    setSlugTouched(false);
    setTagInput("");
    setSaveError(null);
    setComments([]);
    setMode("add");
  }

  function startEdit(post) {
    setForm({
      ...emptyPost,
      ...post,
      tags: Array.isArray(post.tags) ? post.tags : [],
    });
    setEditingId(post.id);
    setSlugTouched(true);
    setTagInput("");
    setSaveError(null);
    setMode("edit");
    loadComments(post.id);
  }

  function cancelForm() {
    setMode("list");
    setForm(emptyPost);
    setEditingId(null);
    setSlugTouched(false);
    setTagInput("");
    setSaveError(null);
    setComments([]);
  }

  /* ------------------------------ FIELD HANDLERS ------------------------------ */
  const handleField = (e) => {
    const { name, value } = e.target;
    setForm((f) => {
      const next = { ...f, [name]: value };
      if (name === "title" && !slugTouched) {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const handleSlugField = (e) => {
    setSlugTouched(true);
    setForm((f) => ({ ...f, slug: slugify(e.target.value) }));
  };

  /* ------------------------------ TAGS ------------------------------ */
  function commitTagInput() {
    const raw = tagInput.trim();
    if (!raw) return;
    const newTags = raw
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    setForm((f) => {
      const existing = f.tags.map((t) => t.toLowerCase());
      const merged = [
        ...f.tags,
        ...newTags.filter((t) => !existing.includes(t.toLowerCase())),
      ];
      return { ...f, tags: merged };
    });
    setTagInput("");
  }

  const handleTagKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commitTagInput();
    }
    if (e.key === "Backspace" && !tagInput && form.tags.length > 0) {
      e.preventDefault();
      setForm((f) => ({ ...f, tags: f.tags.slice(0, -1) }));
    }
  };

  const removeTag = (tag) => {
    setForm((f) => ({ ...f, tags: f.tags.filter((t) => t !== tag) }));
  };

  /* ------------------------------ IMAGE UPLOAD ------------------------------ */
  async function uploadImage(file) {
    const dataBase64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result !== "string")
          return reject(new Error("Unexpected result"));
        const [, base64Part] = result.split("base64,");
        if (!base64Part) return reject(new Error("Invalid data URL"));
        resolve(base64Part);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const res = await fetch("/.netlify/functions/upload-image", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        filename: file.name,
        contentType: file.type || "application/octet-stream",
        dataBase64,
      }),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Upload failed (${res.status})`);
    const url = data.url || data.publicUrl || data.href;
    if (!url) throw new Error("Upload failed: no URL in response");
    return url;
  }

  async function handleCoverFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingCover(true);
      const url = await uploadImage(file);
      setForm((prev) => ({ ...prev, coverImageUrl: url }));
    } catch (err) {
      console.error("Cover upload failed:", err);
      setSaveError(err.message || "Cover upload failed.");
    } finally {
      setUploadingCover(false);
    }
  }

  function handleBodyChange(html) {
    setForm((prev) => ({ ...prev, body: html }));
  }

  /* ------------------------------ SAVE ------------------------------ */
  async function savePost(e) {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);

    try {
      const res = await fetch("/.netlify/functions/save-post", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      const json = await res.json();

      if (!res.ok || !json.ok) {
        setSaveError(json.error || "Failed to save post.");
        setSaving(false);
        return;
      }

      const saved = json.post;
      setPosts((list) => {
        const idx = list.findIndex((p) => p.id === saved.id);
        if (idx >= 0) {
          const updated = [...list];
          updated[idx] = { ...list[idx], ...saved };
          return updated;
        }
        return [saved, ...list];
      });

      cancelForm();
    } catch (err) {
      setSaveError("Network error while saving.");
    } finally {
      setSaving(false);
    }
  }

  /* ------------------------------ DELETE ------------------------------ */
  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const res = await fetch("/.netlify/functions/delete-post", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id: deleteTarget.id }),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) {
        setDeleteError(json.error || "Failed to delete post.");
        setDeleting(false);
        return;
      }
      setPosts((arr) => arr.filter((p) => p.id !== deleteTarget.id));
      setDeleteTarget(null);
      setDeleting(false);
    } catch (err) {
      setDeleteError("Network error while deleting.");
      setDeleting(false);
    }
  }

  /* ------------------------------ COMMENTS ------------------------------ */
  async function loadComments(postId) {
    setCommentsLoading(true);
    setCommentsError(null);
    try {
      const res = await fetch(
        `/.netlify/functions/get-comments?postId=${encodeURIComponent(postId)}`,
        { credentials: "include" }
      );
      const json = await res.json();
      if (!res.ok || !json.ok) {
        setCommentsError(json.error || "Failed to load comments.");
        setComments([]);
      } else {
        setComments(json.comments || []);
      }
    } catch (err) {
      setCommentsError("Network error while loading comments.");
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  }

  async function moderateComment(id, action) {
    try {
      const res = await fetch("/.netlify/functions/moderate-comment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id, action }),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) {
        setCommentsError(json.error || "Failed to update comment.");
        return;
      }
      if (action === "delete") {
        setComments((arr) => arr.filter((c) => c.id !== id));
      } else {
        setComments((arr) =>
          arr.map((c) => (c.id === id ? { ...c, status: json.status } : c))
        );
      }
    } catch (err) {
      setCommentsError("Network error while updating comment.");
    }
  }

  /* #######################################################
     RENDER
  ####################################################### */
  if (loading && mode === "list") return <p>Loading posts…</p>;
  if (loadError && mode === "list") return <p className="av-error">{loadError}</p>;

  /* ------------------------------ FORM MODE ------------------------------ */
  if (mode === "add" || mode === "edit") {
    return (
      <div className="admin-events">
        <h1>{mode === "add" ? "Add New Post" : "Edit Post"}</h1>

        <form className="av-form" onSubmit={savePost}>
          {/* BASIC INFO */}
          <section className="av-section">
            <h2 className="av-section-title">Basic Info</h2>

            <div className="av-row">
              <div className="av-field">
                <label>Title</label>
                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleField}
                  required
                />
              </div>
            </div>

            <div className="av-row">
              <div className="av-field">
                <label>URL Slug</label>
                <input
                  type="text"
                  name="slug"
                  value={form.slug}
                  onChange={handleSlugField}
                  placeholder="auto-generated-from-title"
                />
                <p className="av-hint">
                  Public URL: /blog/{form.slug || "your-post"}
                </p>
              </div>

              <div className="av-field">
                <label>Author</label>
                <input
                  type="text"
                  name="author"
                  value={form.author}
                  onChange={handleField}
                  placeholder="e.g. Jane Doe"
                />
              </div>
            </div>

            <div className="av-row">
              <div className="av-field">
                <label>Category</label>
                <input
                  type="text"
                  name="category"
                  value={form.category}
                  onChange={handleField}
                  placeholder="e.g. Reflections, News"
                />
              </div>

              <div className="av-field">
                <label>Published Date</label>
                <input
                  type="date"
                  name="publishedDate"
                  value={form.publishedDate}
                  onChange={handleField}
                />
              </div>

              <div className="av-field">
                <label>Status</label>
                <select name="status" value={form.status} onChange={handleField}>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>

            <div className="av-row">
              <div className="av-field">
                <label>Excerpt</label>
                <input
                  type="text"
                  name="excerpt"
                  value={form.excerpt}
                  onChange={handleField}
                  placeholder="Short summary shown on the blog index"
                />
              </div>
            </div>
          </section>

          {/* COVER IMAGE */}
          <section className="av-section">
            <h2 className="av-section-title">Cover Image</h2>
            <div className="ae-cover-upload-row">
              <input
                type="file"
                accept="image/*"
                onChange={handleCoverFileChange}
                disabled={uploadingCover}
              />
              {uploadingCover && (
                <span className="ae-cover-upload-status">Uploading…</span>
              )}
            </div>
            {form.coverImageUrl && (
              <div className="ae-cover-preview">
                <img src={form.coverImageUrl} alt="Cover preview" />
                <div>
                  <button
                    type="button"
                    className="av-button av-button-outline"
                    onClick={() =>
                      setForm((prev) => ({ ...prev, coverImageUrl: "" }))
                    }
                  >
                    Remove cover
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* TAGS */}
          <section className="av-section">
            <h2 className="av-section-title">Tags</h2>
            <div className="av-field-tags">
              <label>Tags</label>
              <div className="av-tags-container">
                {form.tags.map((t) => (
                  <span key={t} className="av-tag">
                    {t}
                    <button
                      type="button"
                      className="av-tag-remove"
                      onClick={() => removeTag(t)}
                    >
                      ✕
                    </button>
                  </span>
                ))}
                <input
                  className="av-tag-input"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKey}
                  onBlur={commitTagInput}
                  placeholder={form.tags.length === 0 ? "Type and press Enter…" : ""}
                />
              </div>
            </div>
          </section>

          {/* BODY */}
          <section className="av-section">
            <h2 className="av-section-title">Post Content</h2>
            <TipTapEditor
              value={form.body}
              onChange={handleBodyChange}
              onUploadImage={uploadImage}
            />
          </section>

          {saveError && <p className="av-error">{saveError}</p>}

          <div className="av-actions">
            <button className="av-button av-button-primary" disabled={saving}>
              {saving ? "Saving…" : "Save Post"}
            </button>
            <button
              type="button"
              className="av-button av-button-outline"
              onClick={cancelForm}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </form>

        {/* COMMENT MODERATION (edit only) */}
        {mode === "edit" && (
          <section className="av-section" style={{ maxWidth: 900, marginTop: 16 }}>
            <h2 className="av-section-title">Comments</h2>
            {commentsLoading ? (
              <p className="av-hint">Loading comments…</p>
            ) : commentsError ? (
              <p className="av-error">{commentsError}</p>
            ) : comments.length === 0 ? (
              <p className="av-hint">No comments yet.</p>
            ) : (
              <div className="av-list">
                {comments.map((c) => (
                  <div key={c.id} className="av-card">
                    <div className="av-card-main">
                      <strong>{c.name}</strong>
                      <span className={`av-pill av-pill-${c.status === "approved" ? "published" : "draft"}`}>
                        {c.status}
                      </span>
                      <p className="av-card-meta" style={{ whiteSpace: "pre-wrap" }}>
                        {c.body}
                      </p>
                      {c.createdAt && (
                        <p className="av-card-meta">
                          {new Date(c.createdAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <div className="av-card-actions">
                      {c.status === "approved" ? (
                        <button
                          type="button"
                          className="av-button av-button-outline"
                          onClick={() => moderateComment(c.id, "unapprove")}
                        >
                          Unapprove
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="av-button av-button-primary"
                          onClick={() => moderateComment(c.id, "approve")}
                        >
                          Approve
                        </button>
                      )}
                      <button
                        type="button"
                        className="av-button av-button-danger-soft"
                        onClick={() => moderateComment(c.id, "delete")}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {deleteTarget && (
          <DeleteModal
            item={deleteTarget}
            deleting={deleting}
            error={deleteError}
            onCancel={() => setDeleteTarget(null)}
            onConfirm={confirmDelete}
          />
        )}
      </div>
    );
  }

  /* ------------------------------ LIST MODE ------------------------------ */
  return (
    <div className="admin-events">
      <h1>Blog</h1>

      <div className="av-toolbar">
        <input
          className="av-search"
          placeholder="Search posts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button className="av-button av-button-primary" onClick={startAdd}>
          Add Post
        </button>
      </div>

      {filtered.length === 0 ? (
        <p className="av-hint">No posts found.</p>
      ) : (
        <div className="av-list">
          {filtered.map((p) => (
            <div key={p.id} className="av-card">
              <div className="av-card-main">
                <strong>{p.title}</strong>
                <span className={`av-pill av-pill-${p.status}`}>{p.status}</span>
                <p className="av-card-meta">
                  {p.category ? "Category: " + p.category : "Uncategorized"}
                  {p.publishedDate && " • " + p.publishedDate}
                  {" • "}
                  {p.commentCount || 0} comment
                  {(p.commentCount || 0) === 1 ? "" : "s"}
                  {p.pendingCommentCount > 0 &&
                    ` • ${p.pendingCommentCount} pending`}
                  {(p.tags || []).length > 0 &&
                    " • Tags: " + (p.tags || []).join(", ")}
                </p>
              </div>
              <div className="av-card-actions">
                <button
                  className="av-button av-button-outline"
                  onClick={() => startEdit(p)}
                >
                  Edit
                </button>
                <button
                  className="av-button av-button-danger-soft"
                  onClick={() => setDeleteTarget(p)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteTarget && (
        <DeleteModal
          item={deleteTarget}
          deleting={deleting}
          error={deleteError}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}

/* #######################################################
   DELETE MODAL
####################################################### */
function DeleteModal({ item, deleting, error, onCancel, onConfirm }) {
  return (
    <div className="av-modal-overlay">
      <div className="av-modal">
        <h2>Delete Post</h2>
        <p>
          Are you sure you want to delete <strong>{item.title}</strong>? This
          also removes its comments and cannot be undone.
        </p>
        {error && <p className="av-error">{error}</p>}
        <div className="av-modal-actions">
          <button className="av-button av-button-outline" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="av-button av-button-danger"
            disabled={deleting}
            onClick={onConfirm}
          >
            {deleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}
